/**
 * PolygonDrawer Component
 * Interactive polygon drawing tool for Leaflet maps
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 741-861)
 * Handles click-to-draw, double-click-to-finish, ESC to cancel
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { CircleMarker, Polyline } from 'react-leaflet';

export interface PolygonDrawerProps {
  isDrawing: boolean;
  vertices: [number, number][];
  onAddVertex: (vertex: [number, number]) => void;
  onFinish: () => void;
  onCancel: () => void;
  color?: string;
  fillColor?: string;
}

/**
 * Interactive polygon drawing component
 * Renders vertex markers and connecting lines as user draws
 */
export function PolygonDrawer({
  isDrawing,
  vertices,
  onAddVertex,
  onFinish,
  onCancel,
  color = '#EBFD03',
  fillColor = '#EBFD03',
}: PolygonDrawerProps) {
  const map = useMap();
  const [cursorPos, setCursorPos] = useState<[number, number] | null>(null);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isHoveringOrigin, setIsHoveringOrigin] = useState(false);

  /**
   * Snap a point to be orthogonal (perpendicular or parallel) to the previous edge
   * @param vertices All existing vertices
   * @param to Target point (mouse position)
   * @returns Snapped point at same distance but orthogonal to previous edge
   */
  const snapToOrthogonal = (vertices: [number, number][], to: [number, number]): [number, number] => {
    if (vertices.length === 0) return to;

    const lastVertex = vertices[vertices.length - 1];
    const [lat1, lng1] = lastVertex;
    const [lat2, lng2] = to;

    // Calculate current angle and distance
    const dx = lng2 - lng1;
    const dy = lat2 - lat1;
    const currentAngle = Math.atan2(dy, dx) * (180 / Math.PI);
    const distance = Math.sqrt(dx * dx + dy * dy);

    let snappedAngle = currentAngle;

    // If we have at least 2 vertices, snap relative to the previous edge
    if (vertices.length >= 2) {
      const prevVertex = vertices[vertices.length - 2];
      const [lat0, lng0] = prevVertex;

      // Calculate angle of previous edge
      const prevDx = lng1 - lng0;
      const prevDy = lat1 - lat0;
      const prevEdgeAngle = Math.atan2(prevDy, prevDx) * (180 / Math.PI);

      // Possible snap angles: parallel or perpendicular to previous edge
      const snapOptions = [
        prevEdgeAngle,           // Parallel (same direction)
        prevEdgeAngle + 90,      // Perpendicular (right)
        prevEdgeAngle - 90,      // Perpendicular (left)
        prevEdgeAngle + 180,     // Parallel (opposite direction)
      ];

      // Find closest snap angle to current mouse angle
      snappedAngle = snapOptions.reduce((prev, curr) => {
        // Normalize angles to -180 to 180 for comparison
        const normalizeDiff = (a: number, b: number) => {
          let diff = a - b;
          while (diff > 180) diff -= 360;
          while (diff < -180) diff += 360;
          return Math.abs(diff);
        };

        return normalizeDiff(curr, currentAngle) < normalizeDiff(prev, currentAngle) ? curr : prev;
      });
    } else {
      // Only 1 vertex: snap to cardinal directions (0°, 90°, 180°, 270°)
      const cardinalAngles = [0, 90, 180, -90];
      snappedAngle = cardinalAngles.reduce((prev, curr) => {
        return Math.abs(curr - currentAngle) < Math.abs(prev - currentAngle) ? curr : prev;
      });
    }

    // Calculate snapped position
    const snappedAngleRad = snappedAngle * (Math.PI / 180);
    const snappedLng = lng1 + distance * Math.cos(snappedAngleRad);
    const snappedLat = lat1 + distance * Math.sin(snappedAngleRad);

    return [snappedLat, snappedLng];
  };

  // Check if a point is close to the origin (first vertex) - for click-to-close
  const isNearOrigin = (clickLatLng: { lat: number; lng: number }): boolean => {
    if (vertices.length < 3) return false;

    const origin = vertices[0];
    // Use map zoom to calculate appropriate threshold
    const zoom = map.getZoom();
    // At zoom 20, ~0.00001 is about 1 meter. Scale threshold based on zoom.
    const threshold = 0.0001 * Math.pow(2, 18 - zoom);

    const distance = Math.sqrt(
      Math.pow(clickLatLng.lat - origin[0], 2) +
      Math.pow(clickLatLng.lng - origin[1], 2)
    );

    return distance < threshold;
  };

  useEffect(() => {
    if (!isDrawing) {
      setCursorPos(null);
      return;
    }

    const handleClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();

      // Check if clicking near origin to close polygon
      if (isNearOrigin(e.latlng)) {
        onFinish();
        return;
      }

      let vertex: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Apply orthogonal snapping if Shift is held and we have a previous vertex
      if (e.originalEvent.shiftKey && vertices.length > 0) {
        vertex = snapToOrthogonal(vertices, vertex);
      }

      onAddVertex(vertex);
    };

    const handleDblClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      if (vertices.length >= 3) {
        onFinish();
      }
    };

    const handleMouseMove = (e: any) => {
      const rawPos: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Check if hovering near origin for visual feedback
      setIsHoveringOrigin(isNearOrigin(e.latlng));

      // Apply snap preview if Shift is held and we have a previous vertex
      if (e.originalEvent.shiftKey && vertices.length > 0) {
        const snappedPos = snapToOrthogonal(vertices, rawPos);
        setCursorPos(snappedPos);
        setIsShiftPressed(true);
      } else {
        setCursorPos(rawPos);
        setIsShiftPressed(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && vertices.length >= 3) {
        onFinish();
      } else if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);
    map.on('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
      map.off('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing, map, vertices, onAddVertex, onFinish, onCancel]);

  if (!isDrawing) return null;

  return (
    <>
      {/* Vertex markers */}
      {vertices.map((vertex, index) => {
        const isOrigin = index === 0;
        const canClose = isOrigin && vertices.length >= 3;
        const isOriginHighlighted = canClose && isHoveringOrigin;

        return (
          <CircleMarker
            key={`vertex-${index}`}
            center={vertex}
            // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
            radius={isOriginHighlighted ? 10 : (canClose ? 8 : 6)}
            pathOptions={{
              color: isOriginHighlighted ? '#22c55e' : (canClose ? '#10b981' : color),
              fillColor: isOriginHighlighted ? '#22c55e' : (canClose ? '#10b981' : fillColor),
              fillOpacity: 1,
              weight: isOriginHighlighted ? 3 : 2,
            }}
          />
        );
      })}

      {/* Polyline connecting vertices - increased weight for visibility at high zoom */}
      {vertices.length > 1 && (
        <Polyline
          positions={vertices}
          pathOptions={{
            color,
            weight: 3,
            dashArray: '8, 4',
            opacity: 1,
          }}
        />
      )}

      {/* Preview line from last vertex to cursor */}
      {vertices.length > 0 && cursorPos && (
        <Polyline
          positions={[vertices[vertices.length - 1], cursorPos]}
          pathOptions={{
            color: isShiftPressed ? '#f97316' : color, // Orange when snapping
            weight: isShiftPressed ? 4 : 3,
            dashArray: isShiftPressed ? '10, 5' : '8, 4',
            opacity: isShiftPressed ? 1 : 0.8,
          }}
        />
      )}

      {/* Closing line preview when ready to finish */}
      {vertices.length >= 3 && cursorPos && (
        <Polyline
          positions={[cursorPos, vertices[0]]}
          pathOptions={{
            color: '#22c55e',
            weight: isHoveringOrigin ? 4 : 3,
            dashArray: isHoveringOrigin ? undefined : '8, 4',
            opacity: isHoveringOrigin ? 1 : 0.6,
          }}
        />
      )}
    </>
  );
}
